import { Component, Inject, Input, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomerDto, CustomerListDto } from '@careapp-group/ng-sdk-v2-new';
import { Store } from '@ngrx/store';
import * as selectors from '../../store/reducers/index';
import { Observable, Subscription } from 'rxjs';
import { getIsFamily } from '../../store/organisations/organisations.selectors';

export interface CustomerSelectDialogData {
  hideUnassigned: boolean;
  multi: boolean;
  disableNoMedia: boolean;
  selectedIds: string[];
  selectedCustomerListIds: string[];
  initialTab: 'all' | 'my' | 'lists';
}

export interface CustomerSelectDialogResponse {
  selectedIds: string[];
  selectedCustomerListIds: string[];
}

@Component({
  selector: 'ca-customer-select-dialog',
  templateUrl: './customer-select-dialog.component.html',
  styleUrls: ['./customer-select-dialog.component.scss'],
})
export class CustomerSelectDialogComponent implements OnDestroy {
  private _customerListEntities: { [id: string]: CustomerListDto } = {};
  public customerLists$: Observable<{ [id: string]: CustomerListDto }>;
  public isFamily$: Observable<boolean>;
  public customerListsSubscription: Subscription;
  @Input() multi;
  @Input() disableNoMedia: boolean;
  @Input() hideUnassigned: boolean;
  @Input()
  public selectedIdPanelOpenState = true;
  private _selectedIds: string[];
  public get selectedIds(): string[] {
    return this._selectedIds ?? [];
  }
  public set selectedIds(value: string[]) {
    if (!value || !Array.isArray(value)) {
      this.selectedIds = [];
    } else {
      // Copy to avoid mutation
      this._selectedIds = [...value];
    }
  }
  @Input()
  private _selectedCustomerListIds: string[];
  public get selectedCustomerListIds(): string[] {
    return this._selectedCustomerListIds;
  }
  public set selectedCustomerListIds(value: string[]) {
    if (!value || !Array.isArray(value)) {
      this.selectedCustomerListIds = [];
    } else {
      // Copy to avoid mutation
      this._selectedCustomerListIds = [...value];
    }
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CustomerSelectDialogData,
    private store: Store<any>,
    private dialogRef: MatDialogRef<CustomerSelectDialogComponent>
  ) {
    this.multi = data.multi;
    this.disableNoMedia = data.disableNoMedia;
    this.hideUnassigned = data.hideUnassigned;
    this.selectedIds = data.selectedIds || [];
    this.selectedCustomerListIds = data.selectedCustomerListIds || [];

    this.customerLists$ = this.store.select(selectors.getEntities(selectors.CUSTOMER_LISTS));
    this.isFamily$ = this.store.select(getIsFamily);

    this.customerListsSubscription = this.customerLists$.subscribe(
      (data) => (this._customerListEntities = data)
    );
  }

  ngOnDestroy() {
    this.customerListsSubscription.unsubscribe();
  }

  pruneEmptyCustomerLists() {
    this.selectedCustomerListIds = [...this.selectedCustomerListIds].filter((customerListId) =>
      this._customerListEntities[customerListId].customerIds.some((customerId) =>
        this.selectedIds.includes(customerId)
      )
    );
  }

  toggle(customer: CustomerDto) {
    if (this.multi) {
      this.selectedIds = !!this.selectedIds.find((id) => id === customer.id)
        ? this.selectedIds.filter((id) => id !== customer.id)
        : [...this.selectedIds, customer.id];

      // Remove any customer lists where there no longer exists any selected customers
      this.pruneEmptyCustomerLists();
    } else {
      this.selectedIds = [customer.id];
    }
  }

  toggleCustomerList(customerList: CustomerListDto) {
    // Being a short-hand for multiple customers, multi must be true for customer lists
    if (this.multi) {
      let newSelectedIds = [...this.selectedIds];
      let newSelectedCustomerListIds = [...this.selectedCustomerListIds];

      if ((this.selectedCustomerListIds ?? []).includes(customerList.id)) {
        // Remove from list
        newSelectedIds = newSelectedIds.filter((id) => !customerList.customerIds.includes(id));
        newSelectedCustomerListIds = this.selectedCustomerListIds.filter(
          (id) => id !== customerList.id
        );
        for (let i = 0; i < newSelectedCustomerListIds.length; i++) {
          const customerListId = newSelectedCustomerListIds[i];
          const customerList = this._customerListEntities[customerListId];
          if (customerList != null) {
            newSelectedIds = Array.from(new Set([...newSelectedIds, ...customerList.customerIds]));
          }
        }
      } else {
        // Add to list
        newSelectedIds = Array.from(new Set([...this.selectedIds, ...customerList.customerIds]));
        newSelectedCustomerListIds = Array.from(
          new Set([...this.selectedCustomerListIds, customerList.id])
        );
      }

      this.selectedIds = newSelectedIds;
      this.selectedCustomerListIds = newSelectedCustomerListIds;
    }
  }

  // For passing into the filter pipe, to filter a main list of customers by
  // who can and cannot have media posted. If null, does not filter.
  customerAllowsMediaFilter(allowMemoImages?: boolean) {
    return (item: CustomerDto) => {
      if (allowMemoImages == null) {
        return true;
      }
      return item && item.settings?.allowMemoImages === allowMemoImages;
    };
  }

  submitAndClose(selectedCustomerEntities: CustomerDto[]) {
    if (this.disableNoMedia === true) {
      this.selectedIds = selectedCustomerEntities
        .filter((customer) => customer.settings.allowMemoImages !== false)
        .map((customer) => customer.id);

      // Remove any customer lists where there no longer exists any selected customers
      this.pruneEmptyCustomerLists();
    }

    this.dialogRef.close({
      selectedIds: this.selectedIds,
      selectedCustomerListIds: this.selectedCustomerListIds,
    } as CustomerSelectDialogResponse);
  }
}
